/**
 * The records lens: what the map reveals under a node. A row per record with how far it got (✓ ◷ ✕ per linked
 * data model, the time after its earliest document), its outcome from the farthest hop ("failed at Finance ·
 * invoice", never "done" with a failure at the end), the kinds to choose from as pills with their counts, and the
 * records stuck at a step as chips. Ported from components/data/Records.tsx's RecordTable, re-skinned: no frame,
 * hairline rows, mono keys.
 */
import { ScrollX } from '../ScrollX.tsx';
import { useEffect, useMemo, useState } from 'react';
import type { RecordKind, RecordList, RecordRow, Run } from '../../lib/api.ts';
import { article, count, humanize } from '../../lib/format.ts';
import { freshness } from '../../lib/freshness.ts';
import { useNow } from '../../lib/asof.tsx';
import { Chip, NounIcon, Pill, SystemBadge, cx, noun } from '../../design/primitives.tsx';
import { Counted, NounCount, EmptyLens, LastSeen, LensFrame, MorePill, PillTrack, StepCell, StepHead, compact, plural, recordsOf, showValue } from './common.tsx';
import { baseOf, failedSteps, outcomeOf, type Outcome, type OutcomeState } from './outcome.ts';

const SHOWN = 60;

/** Only the records whose step is missing (late) or pending (not late yet). */
export interface RecordFilter { step: number; state: 'missing' | 'pending' }

const TH = 'text-left text-micro uppercase tracking-[.08em] font-medium text-n7 pb-2 pr-4 border-b border-n4 whitespace-nowrap align-bottom';
const TD = 'py-2.5 pr-4 whitespace-nowrap align-middle';

export function RecordsLens({ list, kinds, runs, onKind, onOpen, onNarrow, filter, extra = 0, onExtra, onShowAll, onClose }: {
  list: RecordList;
  /** Every kind of record found; several make the kind chooser. */
  kinds: RecordKind[];
  /** The runs of what was found: they say at which step a record failed. */
  runs: Run[];
  onKind: (cls: string) => void;
  /** A row opens the record (a peek). */
  onOpen: (row: RecordRow, list: RecordList) => void;
  /** A problem chip narrows the rows to a step's state; null clears. The page may hold this in the URL and pass it back as `filter`. */
  onNarrow?: (filter: RecordFilter | null) => void;
  filter?: RecordFilter | null;
  /** Rows shown beyond the first SHOWN ("show 200 more"), and its change: the page keeps it in the URL. */
  extra?: number;
  onExtra?: (extra: number) => void;
  /** The list is truncated: the page reloads it with a bigger limit. */
  onShowAll?: () => void;
  onClose?: () => void;
}) {
  const now = useNow();
  const [only, setOnly] = useState<RecordFilter | null>(filter ?? null);
  useEffect(() => setOnly(filter ?? null), [filter?.step, filter?.state]); // eslint-disable-line react-hooks/exhaustive-deps
  const [extraLocal, setExtraLocal] = useState(0);
  const shown = SHOWN + (onExtra ? extra : extraLocal);
  const moreRows = (n: number) => (onExtra ? onExtra(n) : setExtraLocal(n));

  const rows = useMemo(() => list.rows.filter((r) => !only || r.steps[only.step] === only.state), [list, only]);
  // Steps that records are missing or waiting for: the quick filters
  const problems = list.steps.flatMap((st, i) => (['missing', 'pending'] as const).map((state) => ({
    step: i, state, st, n: list.rows.filter((r) => r.steps[i] === state).length,
  }))).filter((p) => p.n > 0);
  const pick = (f: RecordFilter) => {
    const next = only && only.step === f.step && only.state === f.state ? null : f;
    setOnly(next);
    onNarrow?.(next);
  };
  const onlyStep = only ? list.steps[only.step] : null;
  // The step deltas count from the record's earliest document: the step that usually comes first (a webshop order
  // before the sales order), else the record's own kind
  const origin = [...list.steps].filter((st) => st.typicalMs !== null && st.typicalMs < 0).sort((a, b) => a.typicalMs! - b.typicalMs!)[0]?.cls ?? list.cls;

  const sentence = (
    <>
      {only && onlyStep ? <><NounCount kind="record" n={list.records} label={`${count(rows.length)} of ${recordsOf(list)}`} /> · {only.state === 'missing' ? 'without' : 'waiting for'} {article(noun(onlyStep.cls))} {noun(onlyStep.cls)}</>
        : <NounCount kind="record" n={list.records} label={recordsOf(list)} />}
      {list.truncated && <> · newest {count(list.rows.length)} listed</>}
      {kinds.length > 1 && <> · also in {kinds.length - 1} other {plural(kinds.length - 1, 'data model')}</>}
    </>
  );

  return (
    <LensFrame kind="record" title={noun(list.cls, 2)} count={sentence} onClose={onClose}>
      {kinds.length > 1 && (
        <PillTrack className="self-start">
          {kinds.map((k) => (
            <Pill key={k.cls} active={k.cls === list.cls} onClick={() => onKind(k.cls)}>
              <span className="inline-flex items-center gap-1.5">
                <SystemBadge code={k.system} size={20} /><span className="font-mono tabular-nums">{recordsOf(k)}</span>{noun(k.cls, k.records)}
              </span>
            </Pill>
          ))}
        </PillTrack>
      )}
      {problems.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {problems.map((p) => {
            const on = only?.step === p.step && only.state === p.state;
            return (
              <Chip key={`${p.step}${p.state}`} tone={p.state === 'missing' ? 'fail' : 'warn'} aria-pressed={on} onClick={() => pick({ step: p.step, state: p.state })}
                className={cx(on && 'shadow-[inset_0_0_0_1.5px_var(--accent)]')}>
                {count(p.n)} {noun(list.cls, p.n)} {p.state === 'missing' ? 'without' : 'waiting for'} {article(noun(p.st.cls))} {noun(p.st.cls)}
              </Chip>
            );
          })}
        </div>
      )}

      {rows.length === 0 ? (
        <EmptyLens>{only && onlyStep ? `Every ${noun(list.cls)} got its ${noun(onlyStep.cls)}.` : `No ${noun(list.cls, 2)} here.`}</EmptyLens>
      ) : (
        <ScrollX className="-mx-6 px-6">
          <table className="w-full border-collapse text-body">
            <thead>
              {list.steps.length > 0 && (
                <tr>
                  <th colSpan={4} aria-hidden="true" />
                  <th colSpan={list.steps.length} className="text-center text-micro font-normal normal-case tracking-normal text-n7 pb-1.5 whitespace-nowrap">after {noun(origin)}</th>
                  {list.columns.length > 0 && <th colSpan={list.columns.length} aria-hidden="true" />}
                </tr>
              )}
              <tr>
                <th className={TH}>{noun(list.cls)}</th>
                <th className={TH}>first seen</th>
                <th className={TH}>last seen</th>
                <th className={TH}>status</th>
                {list.steps.map((st) => <th key={st.cls} className={cx(TH, 'text-center px-1')}><StepHead step={st} /></th>)}
                {list.columns.map((c) => <th key={c.path} className={cx(TH, 'pl-2 !whitespace-normal min-w-[72px]', c.role === 'number' && 'text-right')}>{humanize(c.label)}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, shown).map((row, n) => {
                const base = baseOf(row);
                const failed = failedSteps(row, list, runs);
                const outcome = outcomeOf(row, list, runs);
                return (
                  <tr key={row.key} onClick={() => onOpen(row, list)}
                    className={cx('cursor-pointer border-b border-n4 transition-colors duration-[var(--t-micro)] ease-mv hover:bg-n3/60 mv-rise',
                      freshness(row.last, list.cadenceMs, now) === 'stale' && 'opacity-50')}
                    style={{ animationDelay: `${Math.min(n, 20) * 12}ms` }}>
                    <td className={cx(TD, 'font-mono text-n10')}>
                      <span className="inline-flex items-center gap-2"><NounIcon kind="record" size={14} />{row.key}</span>
                    </td>
                    <td className={cx(TD, 'font-mono text-small text-n8')}>{compact(row.first)}</td>
                    <td className={TD}><LastSeen last={row.last} usual={list.cadenceMs} /></td>
                    <td className={TD}>
                      <span className="inline-flex items-center gap-2">
                        <RecordStatus o={outcome} />
                        {row.failed > 0 && outcome.state !== 'failed' && outcome.state !== 'held' && <Counted n={row.failed} word="failed" className="text-small text-fail" />}
                      </span>
                    </td>
                    {row.steps.map((cell, i) => <td key={list.steps[i]?.cls ?? i} className={cx(TD, 'text-center px-1')}><StepCell cell={cell} base={base} failed={failed.has(i)} /></td>)}
                    {list.columns.map((c) => (
                      <td key={c.path} className={cx(TD, 'pl-2 text-small min-w-[96px] max-w-[240px] !whitespace-normal [overflow-wrap:anywhere]', c.role === 'number' ? 'text-right font-mono text-n9' : c.role === 'id' ? 'font-mono text-n8' : 'text-n9')}>
                        {showValue(row.values[c.path])}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </ScrollX>
      )}

      {(rows.length > shown || (list.truncated && onShowAll)) && (
        <div className="flex justify-center gap-2 flex-wrap">
          {rows.length > shown && <MorePill onClick={() => moreRows(shown - SHOWN + 200)}>show {count(Math.min(200, rows.length - shown))} more</MorePill>}
          {list.truncated && onShowAll && <MorePill onClick={onShowAll}>show all {recordsOf(list)}</MorePill>}
        </div>
      )}
    </LensFrame>
  );
}

const OUTCOME: Record<OutcomeState, { dot: string; text: string }> = {
  failed: { dot: 'bg-fail', text: 'text-fail' },
  held: { dot: 'bg-warn', text: 'text-warn' },
  overdue: { dot: 'bg-fail', text: 'text-n9' },
  waiting: { dot: 'bg-warn', text: 'text-n9' },
  done: { dot: 'bg-ok', text: 'text-n9' },
  other: { dot: 'bg-n5', text: 'text-n9' },
};

/** What became of the record, as a word with its dot: the farthest hop's outcome. */
function RecordStatus({ o }: { o: Outcome }) {
  const s = OUTCOME[o.state];
  return (
    <span className={cx('inline-flex items-center gap-1.5 text-small whitespace-nowrap', s.text)}>
      <i className={cx('size-1.5 rounded-full shrink-0', s.dot)} aria-hidden="true" />{o.text}
    </span>
  );
}
